/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
/* eslint-disable require-yield */

import { parallel } from 'ember-animated';
import { easeIn, easeOut } from 'ember-animated/easings/cosine';
import move from 'ember-animated/motions/move';
import { fadeIn, fadeOut } from 'ember-animated/motions/opacity';
import fade from 'ember-animated/transitions/fade';
import { toDown, toLeft, toRight, toUp } from 'ember-animated/transitions/move-over';

import Component from '@glimmer/component';

import type TransitionContext from 'ember-animated/-private/transition-context';
import type { Transition } from 'ember-animated/-private/transition';

type TransitionName =
  | 'fade'
  | 'toLeft'
  | 'toRight'
  | 'toDown'
  | 'toUp'
  | 'horizontalList'
  | 'verticalList';
type RuleName = 'horizontalSlide' | 'counter';
export type TransitionDictionary = Record<TransitionName, Transition>;
export type RuleDictionary = Record<RuleName, (args: IRulesArgs) => Transition>;

interface BvAnimatedSignature {
  Args: {
    contained?: boolean;
    tag?: string;
  };
  Blocks: {
    default: [transitions: TransitionDictionary, rules: RuleDictionary];
  };
  Element: HTMLElement;
}

export interface IRulesArgs<I = unknown> {
  firstTime: boolean;
  oldItems: I[];
  newItems: I[];
}

export default class BvAnimatedComponent extends Component<BvAnimatedSignature> {
  get transitions(): TransitionDictionary {
    return {
      fade,
      toLeft,
      toRight,
      toDown,
      toUp,
      horizontalList: this.horizontalList,
      verticalList: this.verticalList
    };
  }

  get rules(): RuleDictionary {
    return {
      horizontalSlide: this.horizontalSlideRules,
      counter: this.counterRules
    };
  }

  horizontalSlideRules({ newItems = [], oldItems = [] }: IRulesArgs): Transition {
    if (oldItems[0] != null && newItems[0] != null && oldItems[0] > newItems[0]) {
      return toRight;
    } else {
      return toLeft;
    }
  }

  counterRules({ oldItems = [], newItems = [] }: IRulesArgs): Transition {
    return oldItems[0] != null && newItems[0] != null && oldItems[0] < newItems[0] ? toUp : toDown;
  }

  *horizontalList({ keptSprites, removedSprites, insertedSprites }: TransitionContext) {
    for (const sprite of insertedSprites) {
      sprite.startTranslatedBy(-sprite.finalBounds!.width / 2, 0);
      // @ts-ignore
      parallel(move(sprite, { easing: easeOut }), fadeIn(sprite, { easing: easeOut }));
    }

    for (const sprite of keptSprites) {
      move(sprite, { easing: easeIn });
    }

    for (const sprite of removedSprites) {
      fadeOut(sprite, { easing: easeIn });
    }
  }

  *verticalList({ keptSprites, insertedSprites, removedSprites }: TransitionContext) {
    for (const sprite of insertedSprites) {
      sprite.startTranslatedBy(0, -sprite.finalBounds!.height / 2);
      // @ts-ignore
      parallel(move(sprite, { easing: easeOut }), fadeIn(sprite, { easing: easeOut }));
    }

    for (const sprite of keptSprites) {
      move(sprite, { easing: easeIn });
    }

    for (const sprite of removedSprites) {
      fadeOut(sprite, { easing: easeIn });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvAnimated: typeof BvAnimatedComponent;
    'bv-animated': typeof BvAnimatedComponent;
  }
}
